import { useState } from 'react';
import { useAuthStore } from '@/store/useAuthStore';
import type { PersonDetail } from '@/api/persons.api';
import type { PersonCondition, PersonTrait } from '@/api/medical.api';
import {
  useConditions,
  useDeleteCondition,
  useTraits,
  useDeleteTrait,
  useConsents,
  useGrantConsent,
  useRevokeConsent,
} from '@/hooks/useMedical';
import {
  INHERITANCE_LABELS,
  CONDITION_CERTAINTY_LABELS,
  TRAIT_CERTAINTY_LABELS,
  TRAIT_TYPE_BY_KEY,
  statusSymbol,
  formatTraitValue,
} from './medical/medicalLabels';
import { ConditionForm } from './medical/ConditionForm';
import { TraitForm } from './medical/TraitForm';
import { useConfirm } from '@/components/common/ConfirmDialog';

interface Props {
  person: PersonDetail;
}

export function MedicalTab({ person }: Props) {
  const canManage = useAuthStore((s) => s.hasSensitivePermission('medical.manage'));
  const personId = person.id;

  const { data: condResp } = useConditions(personId);
  const { data: traits } = useTraits(personId);

  const [condForm, setCondForm] = useState<{ open: boolean; edit?: PersonCondition }>({ open: false });
  const [traitForm, setTraitForm] = useState<{ open: boolean; edit?: PersonTrait }>({ open: false });

  const conditions = condResp?.data ?? [];
  const hereditaryHidden = condResp?.hereditaryHidden ?? 0;

  return (
    <div className="space-y-6">
      {/* Einwilligungs-Banner für lebende Personen */}
      {person.isLiving && <ConsentBanner personId={personId} canManage={canManage} />}

      {/* ─── Erkrankungen ─────────────────────────────────────────────── */}
      <Section
        title="Erkrankungen & Todesursachen"
        onAdd={canManage ? () => setCondForm({ open: true }) : undefined}
        addLabel="Diagnose"
      >
        {conditions.length === 0 ? (
          <Empty text="Keine Diagnosen erfasst." />
        ) : (
          <div className="space-y-2">
            {conditions.map((c) => (
              <ConditionRow
                key={c.id}
                condition={c}
                canManage={canManage}
                onEdit={() => setCondForm({ open: true, edit: c })}
                personId={personId}
              />
            ))}
          </div>
        )}
        {hereditaryHidden > 0 && (
          <p className="mt-2 rounded bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700">
            🔒 {hereditaryHidden} erbliche Diagnose(n) ausgeblendet – erfordert die Berechtigung „Erbkrankheiten & Risiko".
          </p>
        )}
      </Section>

      {/* ─── Merkmale ─────────────────────────────────────────────────── */}
      <Section
        title="Phänotypische Merkmale"
        onAdd={canManage ? () => setTraitForm({ open: true }) : undefined}
        addLabel="Merkmal"
      >
        {(traits ?? []).length === 0 ? (
          <Empty text="Keine Merkmale erfasst." />
        ) : (
          <div className="flex flex-wrap gap-2">
            {(traits ?? []).map((t) => (
              <TraitChip
                key={t.id}
                trait={t}
                canManage={canManage}
                onEdit={() => setTraitForm({ open: true, edit: t })}
                personId={personId}
              />
            ))}
          </div>
        )}
      </Section>

      <p className="border-t border-slate-100 pt-3 text-[11px] leading-relaxed text-slate-400">
        Hinweis: Angaben sind genealogische Aufzeichnungen, keine medizinische Diagnose. Berechnete Vererbungs-/Risikoaussagen
        ersetzen keine humangenetische Beratung.
      </p>

      {condForm.open && (
        <ConditionForm personId={personId} condition={condForm.edit} onClose={() => setCondForm({ open: false })} />
      )}
      {traitForm.open && (
        <TraitForm personId={personId} trait={traitForm.edit} onClose={() => setTraitForm({ open: false })} />
      )}
    </div>
  );
}

// ─── Bausteine ─────────────────────────────────────────────────────────────

function Section({
  title, addLabel, onAdd, children,
}: {
  title: string;
  addLabel: string;
  onAdd?: () => void;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</h4>
        {onAdd && (
          <button onClick={onAdd} className="flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-blue-600 hover:bg-blue-50">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            {addLabel}
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="text-sm text-slate-400">{text}</p>;
}

function ConditionRow({
  condition, canManage, onEdit, personId,
}: {
  condition: PersonCondition;
  canManage: boolean;
  onEdit: () => void;
  personId: string;
}) {
  const del = useDeleteCondition(personId);
  const confirm = useConfirm();
  const sym = statusSymbol(condition.ascertainment);
  const label = condition.catalog?.label ?? condition.freeText ?? 'Unbenannt';
  const category = condition.catalog?.category;
  const inheritance = condition.catalog?.isHereditary ? condition.catalog.inheritance : null;

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Diagnose entfernen',
      message: <>Diagnose <strong>„{label}"</strong> wirklich entfernen?</>,
      confirmLabel: 'Entfernen',
      variant: 'danger',
    });
    if (ok) del.mutate(condition.id);
  };

  return (
    <div className="flex items-start justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50/50 p-2.5">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-base leading-none ${sym.color}`} title={sym.label}>{sym.symbol}</span>
          <span className="font-medium text-slate-800">{label}</span>
          {condition.isCauseOfDeath && (
            <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-medium text-white">Todesursache</span>
          )}
          {category === 'hereditary' && (
            <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">erblich</span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-slate-500">
          <span>{sym.label}</span>
          {inheritance && <span>· {INHERITANCE_LABELS[inheritance] ?? inheritance}</span>}
          <span>· {CONDITION_CERTAINTY_LABELS[condition.certainty]}</span>
          {condition.catalog?.icd10 && <span>· ICD-10 {condition.catalog.icd10}</span>}
          {condition.diagnosedDateText && <span>· {condition.diagnosedDateText}</span>}
          {condition.sources.length > 0 && <span>· 🔗 {condition.sources.length} Beleg(e)</span>}
        </div>
      </div>
      {canManage && (
        <div className="flex flex-shrink-0 items-center gap-1">
          <IconButton onClick={onEdit} title="Bearbeiten" variant="edit" />
          <IconButton onClick={handleDelete} title="Entfernen" variant="delete" disabled={del.isPending} />
        </div>
      )}
    </div>
  );
}

function TraitChip({
  trait, canManage, onEdit, personId,
}: {
  trait: PersonTrait;
  canManage: boolean;
  onEdit: () => void;
  personId: string;
}) {
  const del = useDeleteTrait(personId);
  const confirm = useConfirm();
  const def = TRAIT_TYPE_BY_KEY[trait.traitType];
  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Merkmal entfernen',
      message: 'Merkmal wirklich entfernen?',
      confirmLabel: 'Entfernen',
      variant: 'danger',
    });
    if (ok) del.mutate(trait.id);
  };
  return (
    <div className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white py-1 pl-3 pr-1.5 text-sm">
      <span className="text-slate-500">{def?.label ?? trait.traitType}:</span>
      <span className="font-medium text-slate-800">{formatTraitValue(trait)}</span>
      <span className="text-[10px] text-slate-400">({TRAIT_CERTAINTY_LABELS[trait.certainty]})</span>
      {canManage && (
        <span className="flex items-center">
          <IconButton onClick={onEdit} title="Bearbeiten" variant="edit" small />
          <IconButton onClick={handleDelete} title="Entfernen" variant="delete" small disabled={del.isPending} />
        </span>
      )}
    </div>
  );
}

function IconButton({
  onClick, title, variant, disabled, small,
}: {
  onClick: () => void;
  title: string;
  variant: 'edit' | 'delete';
  disabled?: boolean;
  small?: boolean;
}) {
  const size = small ? 'h-5 w-5' : 'h-6 w-6';
  const icon = small ? 'h-3 w-3' : 'h-3.5 w-3.5';
  const color = variant === 'delete' ? 'hover:bg-red-50 hover:text-red-500' : 'hover:bg-slate-100 hover:text-slate-700';
  return (
    <button onClick={onClick} disabled={disabled} title={title} className={`flex ${size} items-center justify-center rounded text-slate-400 ${color} disabled:opacity-40`}>
      {variant === 'edit' ? (
        <svg className={icon} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z" />
        </svg>
      ) : (
        <svg className={icon} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      )}
    </button>
  );
}

// ─── Einwilligungs-Banner (lebende Personen, Art. 9 DSGVO) ───────────────────

function ConsentBanner({ personId, canManage }: { personId: string; canManage: boolean }) {
  const { data: consents } = useConsents(personId);
  const grant = useGrantConsent(personId);
  const revoke = useRevokeConsent(personId);

  const active = (consents ?? []).find((c) => c.scope === 'record' && !c.revokedAt);

  if (active) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
        <span>✔ Einwilligung zur Erfassung dokumentiert{active.grantedAt ? ` (${new Date(active.grantedAt).toLocaleDateString('de-DE')})` : ''}.</span>
        {canManage && (
          <button onClick={() => revoke.mutate(active.id)} disabled={revoke.isPending} className="font-medium text-emerald-700 hover:underline disabled:opacity-50">
            widerrufen
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
      <p className="font-medium">Lebende Person – Einwilligung erforderlich</p>
      <p className="mt-0.5">
        Gesundheitsdaten sind besondere Kategorie (Art. 9 DSGVO). Ohne dokumentierte Einwilligung lehnt der Server das Speichern ab.
      </p>
      {canManage && (
        <button
          onClick={() => grant.mutate({ scope: 'record' })}
          disabled={grant.isPending}
          className="mt-1.5 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
        >
          Einwilligung dokumentieren
        </button>
      )}
    </div>
  );
}
